import React, { useState, ChangeEvent } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

// DeleteUserPopup displays a confirmation dialog for account deletion.
// The "Delete Account" button remains disabled until the user types "DELETE" into the text field.
export interface UserStatus {
  hasStripeSub: boolean;
  isOnTrial: boolean;
  hasReferrals: boolean;
}

export interface DeleteUserPopupProps {
  userId: string;
  userName?: string;
  isOpen?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userStatus?: UserStatus;
}

const defaultStatus: UserStatus = {
  hasStripeSub: true,
  isOnTrial: false,
  hasReferrals: true,
};

export const DeleteUserPopup: React.FC<DeleteUserPopupProps> = ({
  userId,
  userName = '',           // default to empty string
  isOpen = true,
  onClose,
  onConfirm,
  userStatus = defaultStatus,
}) => {
  const { hasStripeSub, isOnTrial, hasReferrals } = userStatus;
  const [confirmText, setConfirmText] = useState<string>('');

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setConfirmText(e.target.value);
  };

  const isDeleteTyped = confirmText === 'DELETE';
  // use safe trim on guaranteed string
  const trimmedName = userName.trim();
  const accountLabel = trimmedName === '' ? 'your account' : `account of ${trimmedName}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="p-6 max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <DialogTitle className="text-red-600 text-xl font-bold">
            Confirm Deletion of {accountLabel}
          </DialogTitle>

          <div className="space-y-4 text-sm mt-2">
            <p>
              Are you sure you want to delete {accountLabel}? This action is{' '}
              <span className="font-semibold">permanent</span>. Here's what it means for {accountLabel}:
            </p>

            <ul className="list-disc list-inside space-y-3">
              <li className={`flex gap-2 items-start ${hasStripeSub ? 'text-red-700' : ''}`}>
                {hasStripeSub ? (
                  <AlertTriangle className="text-red-600 mt-1 w-6 h-6 flex-shrink-0" />
                ) : (
                  <CheckCircle className="text-green-600 mt-1 w-6 h-6 flex-shrink-0" />
                )}
                <span>
                  {hasStripeSub ? (
                    <>{trimmedName == ''? 'You have ': trimmedName + ' has '}an active <span className="font-semibold">paid subscription</span>. Deleting {accountLabel} will also cancel your subscription <span className="font-semibold">without any refund</span>. This cannot be undone.</>
                  ) : (
                    <>No active paid subscription found. You're all good here.</>
                  )}
                </span>
              </li>

              <li className={`flex gap-2 items-start ${isOnTrial ? 'text-red-700' : ''}`}>
                {isOnTrial ? (
                  <AlertTriangle className="text-red-600 mt-1 w-6 h-6 flex-shrink-0" />
                ) : (
                  <CheckCircle className="text-green-600 mt-1 w-6 h-6 flex-shrink-0" />
                )}
                <span>
                  {isOnTrial ? (
                    <>{trimmedName == ''? "You're ": trimmedName + '  is '}currently on a <span className="font-semibold">free trial</span>. Deleting {accountLabel} will forfeit this opportunity.</>
                  ) : (
                    <>{trimmedName == ''? "You're ": trimmedName + '  is '}not using a trial at the moment. No trial will be lost.</>
                  )}
                </span>
              </li>

              <li className={`flex gap-2 items-start ${hasReferrals ? 'text-red-700' : ''}`}>
                {hasReferrals ? (
                  <AlertTriangle className="text-red-600 mt-1 w-6 h-6 flex-shrink-0" />
                ) : (
                  <CheckCircle className="text-green-600 mt-1 w-6 h-6 flex-shrink-0" />
                )}
                <span>
                  {hasReferrals ? (
                    <>{trimmedName == ''? "You've ": trimmedName + '  has '}referred friends to the platform. Deleting {accountLabel} will sever these connections and <span className="font-semibold">cancel all upcoming referral rewards</span>.</>
                  ) : (
                    <>{trimmedName == ''? "You've ": trimmedName + '  has '}no active referrals. Nothing to lose here.</>
                  )}
                </span>
              </li>
            </ul>
          </div>

          {/* Confirmation input */}
          <div className="mt-4">
            <label htmlFor="confirm-input" className="block text-sm font-medium text-gray-700">
              Type "DELETE" to enable the button
            </label>
            <input
              id="confirm-input"
              type="text"
              value={confirmText}
              onChange={handleInputChange}
              className="mt-1 block w-full border rounded-md p-2"
              placeholder="Type DELETE to confirm"
            />
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                variant="destructive"
                onClick={onConfirm}
                disabled={!isDeleteTyped}
              >
                Delete {accountLabel}
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
